/**
 * This file was created to be an easier API than focusRing.ts.
 * It'll do some math so you don't need to.
 *
 * It's split out so we don't have to upgrade the whole repo and can move components over to
 * this one over time.
 */

import { ThemeKey } from "@smartsheet/design-tokens";
import { color } from "@smartsheet/design-tokens-react";
import { focus } from "../constants/colors";
import { themed, ThemeMode } from "./theme";

export interface CreateFocusRingProps {
    /**
     * This width will be used to calculate the focus ring's border-radius.
     *
     * Numbers here are assumed to be px values and will be converted: `${num}px`.
     */
    componentBorderWidth: number | string;
    /**
     * Border-radius. If number, assume px. If string, assumes a valid CSS radius value ("initial,
     * inherit, etc.") or that units come with it (32px, etc.).
     */
    componentRadius: number | string;

    /**
     * Distance away from the component.
     */
    gap: number;

    /**
     * The ring's color.
     * This is useful in theming, but shouldn't be overridden usually.
     */
    theme: ThemeMode | ThemeKey;

    /**
     * If specified, overrides the default colors from the theme.
     */
    colorOverride?: string;

    /**
     * If provided, we'll skip the normal calculation to figure out the right border radius.
     */
    componentRadiusOverride?: number | string;

    /**
     * Defaults to ":focus". If provided, this pseudoclass will be used to apply the focus ring.
     *
     * Examples:
     * :focus           => When this element has focus.
     * :focus-within    => When something within this element has focus
     * :focus--visible  => When this element has focus AND the UA decides to show focus.
     * <empty string>   => Always display the ring. Use this if you don't want to attach a CSS-based
     *                     focus class and are going to apply the ring some other way.
     */
    focusSelector?: string;

    /**
     * If specified, overrides the default inner ring color from the theme.
     */
    innerColorOverride?: string;

    /**
     * Position to apply to the root element.
     */
    position?: "relative" | "absolute";

    /**
     * Which pseudoelement to use for the ring. Defaults to ::before.
     * Useful in the case where you have 2 rings on a component i.e. select.
     */
    pseudoElement?: "before" | "after";

    /**
     * border-width
     */
    ringWidth?: number;

    /**
     * The width of the secondary focus ring.
     */
    secondaryWidth?: number;
}

export const focusRingTheme = themed({
    outerColor: {
        "lodestar-light": focus,
        oldestar: "#0073ec",
        "ursa-light": color("focus-indicator--border-primary"),
    },
    innerColor: {
        "lodestar-light": "transparent",
        oldestar: "transparent",
        "ursa-light": color("focus-indicator--border-secondary"),
    },
});

// TODO look into outline-color: none and using multiple box-shadows instead of a border color on :before?

export const createFocusRingStyles = ({
    colorOverride,
    innerColorOverride,
    theme,
    gap,
    componentBorderWidth,
    componentRadius,
    componentRadiusOverride,
    ringWidth = 2,
    position = "relative",
    pseudoElement = "before",
    focusSelector = ":focus",
    secondaryWidth = 1,
}: CreateFocusRingProps): string => {
    // This needs to be a negative number so we'll subtract all of these from 0.
    const borderOffset = `calc(0px - ${px(gap)} - ${px(ringWidth)} - ${px(componentBorderWidth)})`;

    // If this is a string, assume the calculations are already done. Otherwise, calculate the radius.
    const outerBorderRadius =
        componentRadiusOverride || `calc(${px(componentRadius)} + calc(${px(gap)} + ${px(ringWidth)} ))`;

    return `
        & {
            position: ${position};
            outline: none;
        }

        &${focusSelector} {
            outline: none;
        }

        &${focusSelector}::${pseudoElement} {
            content: "";
            display: block;
            
            position: absolute;
            inset: ${borderOffset};

            border: ${ringWidth}px solid ${colorOverride || focusRingTheme.outerColor(theme)};
            border-radius: ${outerBorderRadius};

            pointer-events: none;

            box-shadow: inset 0 0 0 ${secondaryWidth}px ${innerColorOverride || focusRingTheme.innerColor(theme)};
        }
    `;
};

const px = (input: string | number) => (typeof input === "string" ? input : `${input}px`);
